import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  FiCheckCircle,
  FiClock,
  FiAlertTriangle,
  FiShield
} from 'react-icons/fi';
import { LuCalendarCheck } from 'react-icons/lu';

import { fetchChecklistStats } from '../../../features/checks/checkActions';

const RestaurantDashboard = ({ selectedDate }) => {
  const dispatch = useDispatch();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // ============================================================
  // FETCH DASHBOARD STATS
  // ============================================================

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);

        const result = await dispatch(
          fetchChecklistStats(selectedDate)
        ).unwrap();

        setStats(result?.data || null);

      } catch (error) {
        console.error(
          'Failed to fetch dashboard stats:',
          error
        );

        setStats(null);

      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [dispatch, selectedDate]);

  // ============================================================
  // STATS VALUES
  // ============================================================

  const totalTasks = stats?.totalTasks || 0;
  const completedTasks = stats?.completedTasks || 0;
  const actionRequired = stats?.actionRequired || 0;
  const overdueTasks = stats?.overdueTasks || 0;
  const completionPercentage =
    stats?.completionPercentage || 0;

  // ============================================================
  // METRICS
  // ============================================================

  const metrics = [
    {
      title: "Today's Tasks",
      value: totalTasks,
      subtext: "Total tasks due today",
      icon: (
        <LuCalendarCheck className="text-indigo-600 text-lg" />
      ),
      bg: "bg-indigo-50 border-indigo-100"
    },
    {
      title: "Completed",
      value: completedTasks,
      subtext: `${completionPercentage}% of today's tasks`,
      icon: (
        <FiCheckCircle className="text-emerald-600 text-lg" />
      ),
      bg: "bg-emerald-50 border-emerald-100"
    },
    {
      title: "Action Required",
      value: actionRequired,
      subtext: "Tasks requiring attention",
      icon: (
        <FiAlertTriangle className="text-amber-600 text-lg" />
      ),
      bg: "bg-amber-50 border-amber-100"
    },
    {
      title: "Overdue",
      value: overdueTasks,
      subtext: "Require immediate attention",
      icon: (
        <FiClock className="text-red-600 text-lg" />
      ),
      bg: "bg-red-50 border-red-100"
    }
  ];

  // ============================================================
  // RESTAURANT HEALTH
  // ============================================================

  const restaurantHealthy =
    actionRequired === 0 &&
    overdueTasks === 0;

  return (
    <div className="w-full">

      {/* Optional loading indicator */}
      {loading && (
        <div className="mb-3 text-xs text-slate-400">
          Loading dashboard statistics...
        </div>
      )}

      {/* Top Grid Layer */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 items-stretch">

        {/* =====================================================
            LEFT 4 METRIC CARDS
        ====================================================== */}

        <div className="xl:col-span-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

          {metrics.map((card, i) => (
            <div
              key={i}
              className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm flex flex-col justify-between min-h-[140px]"
            >

              <div className="flex flex-col gap-2">

                {/* Icon Container */}
                <div
                  className={`
                    w-9 h-9 rounded-lg
                    flex items-center justify-center
                    border ${card.bg}
                  `}
                >
                  {card.icon}
                </div>

                <div>
                  <p className="text-xs font-semibold text-slate-500">
                    {card.title}
                  </p>

                  <p className="text-2xl font-bold text-slate-800 mt-1">
                    {loading ? '—' : card.value}
                  </p>
                </div>

              </div>

              <p className="text-[11px] text-slate-400 mt-2 line-clamp-2">
                {card.subtext}
              </p>

            </div>
          ))}

        </div>

        {/* =====================================================
            RESTAURANT HEALTH
        ====================================================== */}

        <div className="xl:col-span-1 bg-white border border-slate-100 rounded-xl p-4 shadow-sm flex flex-col justify-between min-h-[140px]">

          <div>

            <p className="text-xs font-bold text-slate-800">
              Restaurant Health
            </p>

            <div className="flex items-center gap-3 mt-3">

              <div
                className={`
                  w-9 h-9 rounded-lg
                  border flex items-center justify-center
                  ${
                    restaurantHealthy
                      ? 'bg-emerald-50 border-emerald-100'
                      : 'bg-amber-50 border-amber-100'
                  }
                `}
              >
                <FiShield
                  className={`
                    text-lg
                    ${
                      restaurantHealthy
                        ? 'text-emerald-600 fill-emerald-600'
                        : 'text-amber-600'
                    }
                  `}
                />
              </div>

              <div>

                <p
                  className={`
                    text-base font-bold leading-none
                    ${
                      restaurantHealthy
                        ? 'text-emerald-600'
                        : 'text-amber-600'
                    }
                  `}
                >
                  {restaurantHealthy
                    ? 'Healthy'
                    : 'Attention Needed'}
                </p>

                <p className="text-[10px] text-slate-400 mt-1">
                  {restaurantHealthy
                    ? 'No critical issues'
                    : 'Some tasks require attention'}
                </p>

              </div>

            </div>

          </div>

          {/* Bottom Alerts Row */}
          <div className="grid grid-cols-2 border-t border-slate-100 pt-3 mt-3 text-center">

            <div className="border-r border-slate-100">

              <p className="text-sm font-bold text-slate-800">
                0
              </p>

              <p className="text-[10px] text-slate-400">
                Critical Alerts
              </p>

            </div>

            <div>

              <p className="text-sm font-bold text-slate-800">
                {actionRequired}
              </p>

              <p className="text-[10px] text-slate-400">
                Advisory Alerts
              </p>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
};

export default RestaurantDashboard;
